import { Activity, useCallback, useMemo, useState } from 'react';
import { useAppSelector } from '@/store/hooks';
import { selectPasscodeLists } from '@/store/slices/audiences/selectors';
import Button from '@ui/buttons/button/button';
import Checkbox from '@ui/checkbox/checkbox';
import SearchInput from '@ui/search-input/search-input';
import Text from '@ui/text/text/text';
import { getDisplayName } from '../../utils';
import Container from '@ui/container/container';
import Popover from '@ui/popover/popover';
import { ICONS } from '@ui/icons/types';
import usePopover from '@ui/popover/use-popover';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getProjectOverChannelColorCssVariable from '@/utils/colors/get-project-over-channel-color-css-variable';
import { Virtuoso } from 'react-virtuoso';

type AddUserToListProps = {
  onSave: (selectedLists: number[]) => void;
};

const AddUserToList = ({ onSave }: AddUserToListProps) => {
  const passcodeLists = useAppSelector(selectPasscodeLists);
  const { setClosePopover } = usePopover({ id: 'add-users-to-list' });
  const [search, setSearch] = useState('');
  const [selectedLists, setSelectedLists] = useState<Set<number>>(new Set());

  const filteredLists = useMemo(() => {
    return search.trim()
      ? passcodeLists.filter((l) => l.name.toLowerCase().includes(search.toLowerCase()))
      : passcodeLists;
  }, [passcodeLists, search]);

  const handleToggle = useCallback((value: string | number, isOn: boolean) => {
    setSelectedLists((prev) => {
      const next = new Set(prev);
      if (isOn) {
        next.add(Number(value));
      } else {
        next.delete(Number(value));
      }
      return next;
    });
  }, []);

  const handleSave = useCallback(() => {
    onSave([...selectedLists]);
    setClosePopover();
  }, [onSave, selectedLists, setClosePopover]);

  return (
    <Container className="flex jc-flex-end padding-r-20" height="0px">
      <Popover
        id="add-users-to-list"
        onTriggerButtonClick={() => {}}
        renderTriggerButton={() => (
          <span aria-hidden="true" style={{ display: 'inline-block', width: 0, height: 0, overflow: 'hidden' }} />
        )}
        preferredPosition="bottom-left"
        closeOnClick={false}
        width="200px"
      >
        {({ open, closing }) => (
          <Activity mode={open || closing ? 'visible' : 'hidden'}>
            <Container className="flex fd-column" style={{ overflow: 'hidden' }}>
              <Container className="padding-h-16 padding-t-12 padding-b-4">
                <SearchInput onSubmit={setSearch} submitOnClear isClearable debounceTime={200} placeholder="Search" />
              </Container>
              <Container style={{ overflowY: 'auto' }} maxHeight="350px">
                <Virtuoso
                  data={filteredLists}
                  data-length={filteredLists.length}
                  totalCount={filteredLists.length}
                  style={{
                    maxHeight: '350px',
                    // 36 is the min-height of each item in the list
                    height: 'calc(36 * attr(data-length px))',
                  }}
                  itemContent={(_, list) => (
                    <Container key={list.passcode_list} className="padding-v-8 padding-h-16 P2">
                      <Checkbox
                        value={list.passcode_list ?? 0}
                        checked={selectedLists.has(list.passcode_list ?? 0)}
                        onChange={handleToggle}
                        label={getDisplayName(list.name)}
                        selectedIconName={ICONS.ROUND_CHECKBOX_SELECTED}
                        unselectedIconName={ICONS.ROUND_CHECKBOX_UNSELECTED}
                      />
                    </Container>
                  )}
                />
                {filteredLists.length === 0 && (
                  <Container className="flex padding-t-16 jc-center">
                    <Text className="P2" design="GlobalIcoText3">
                      No lists match your search
                    </Text>
                  </Container>
                )}
              </Container>
              <Container className="flex fd-row jc-flex-end padding-16 gap-12">
                <Button className="no-style P2 padding-0" onClick={() => setClosePopover()}>
                  Cancel
                </Button>
                <Button className="no-style P2 padding-0" onClick={handleSave} disabled={selectedLists.size === 0}>
                  <Text
                    className="P2"
                    color={getProjectOverChannelColorCssVariable(EColorVariablesV2.primary, 'global-cont-1')}
                  >
                    Save
                  </Text>
                </Button>
              </Container>
            </Container>
          </Activity>
        )}
      </Popover>
    </Container>
  );
};

export default AddUserToList;
